import { UploadOutlined } from '@ant-design/icons';
import { Button, message, Upload } from 'antd';
import { Formik } from 'formik';
import { Form, Input, Select, SubmitButton } from 'formik-antd';
import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import { useNavigate, useParams } from 'react-router-dom';
import ContentWithErrorHandler from '../../app/ContentWithErrorHandler';
import quillConfig from '../../common/quillConfig';
import { useCreateNewTagMutation, useGetTagByTypeQuery } from '../tags/tagsAPI';
import { useCreateNewsMutation, useGetNewsQuery, useUpdateNewsMutation } from './newsAPI';

const { Option } = Select;

interface InitialData {
  title: string;
  isHeadline: boolean;
  isPublished: boolean;
  newsType: 'manual' | 'instagram';
  instagramUrl?: string;
  tags: any[];
}

function NewsDetail() {
  const { id } = useParams();

  const [fileList, setFileList] = useState<any>([]);
  const [content, setContent] = useState('');
  const [initialData, setInitialData] = useState<InitialData>({
    title: '',
    isHeadline: false,
    isPublished: false,
    newsType: 'manual',
    instagramUrl: '',
    tags: [],
  });

  const { data: newsTags, isLoading: newsTagsIsLoading } = useGetTagByTypeQuery('newsTag');
  const {
    data: newsData,
    isLoading: newsDataIsLoading,
    isError: newsDataIsError,
  } = useGetNewsQuery(id || '', {
    skip: id ? false : true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });

  const [createTag] = useCreateNewTagMutation();
  const [updateNews] = useUpdateNewsMutation();
  const [createNews] = useCreateNewsMutation();

  useEffect(() => {
    if (newsData) {
      setInitialData({
        title: newsData.title,
        isHeadline: newsData.isHeadline,
        isPublished: newsData.isPublished,
        newsType: newsData.newsType || 'manual',
        instagramUrl: newsData.instagramUrl || '',
        tags: newsData.tags.map((e) => e.name),
      });
      setContent(newsData.content);
      setFileList(
        newsData.thumbnail != null
          ? [
              {
                uid: '-1',
                name: newsData.title,
                status: 'done',
                url: newsData.thumbnail,
              },
            ]
          : [],
      );
    }
  }, [newsData]);

  const handleUploadChange = async (info) => {
    let fileList = [...info.fileList];
    fileList = fileList.slice(-1);

    fileList = fileList.map((file) => {
      if (file.response) {
        file.url = file.response.url;
      }
      return file;
    });

    setFileList(fileList);
  };

  const validate = (value) => {
    let error;
    if (!value) {
      error = 'Tidak boleh kosong';
    }
    return error;
  };

  const save = async (value: InitialData, closeLoading: any) => {
    var tagsNotExist: string[] = [];
    var existingTags = newsTags?.map((e) => e.name) || [];

    await value.tags.forEach((userInputTag: any) => {
      if (!existingTags.includes(userInputTag)) {
        tagsNotExist.push(userInputTag);
      }
    });

    await tagsNotExist.map(async (tagToAdd) => {
      createTag({ name: tagToAdd, type: 'newsTag' })
        .unwrap()
        .catch((e) => {
          message.error('Gagal membuat tag!');
        });
    });

    const isInstagram = value.newsType === 'instagram';

    if (!isInstagram && id == undefined && fileList.length == 0) {
      closeLoading();
      message.error('Foto sampul tidak boleh kosong');
      return;
    }

    const contentToSend = isInstagram ? value.instagramUrl || content || value.title || 'Instagram feed' : content;
    if (!isInstagram && contentToSend.length === 0) {
      closeLoading();
      message.error('Konten tidak boleh kosong');
      return;
    }

    const formData = new FormData();
    await fileList.map((item) => {
      if (item.originFileObj) {
        formData.append('file', item.originFileObj);
      }
    });

    formData.append('title', value.title);
    formData.append('content', contentToSend);
    formData.append('isPublished', String(value.isPublished));
    formData.append('isHeadline', String(value.isHeadline));
    formData.append('newsType', value.newsType);
    if (value.instagramUrl) {
      formData.append('instagramUrl', value.instagramUrl);
    }

    let tagIds = await value.tags.map((tagName) => {
      let tagObject = newsTags?.find((e) => e.name == tagName);
      if (tagObject) {
        let id = tagObject.id;
        return id;
      }
    });
    formData.append('tagIds', JSON.stringify(tagIds.map((e) => e)));

    let promise;
    if (id) {
      formData.append('id', id);
      promise = updateNews(formData);
    } else {
      promise = createNews(formData);
    }

    promise
      .unwrap()
      .then(() => {
        closeLoading();
        message.success('Berhasil!');
      })
      .catch(() => {
        closeLoading();
        message.error('Gagal!');
      });
  };

  return (
    <div>
      <ContentWithErrorHandler isError={newsDataIsError} isLoading={newsDataIsLoading} withBox={true}>
        <Formik
          initialValues={initialData}
          onSubmit={async (values, actions) => {
            const hide = message.loading('Loading', 0);
            await save(values, hide);
            actions.setSubmitting(false);
          }}
          enableReinitialize={true}
          render={({ values }) => (
            <Form layout="vertical">
              {values.newsType !== 'instagram' && (
                <Form.Item name="photo" label="Foto Sampul">
                  <Upload
                    beforeUpload={(file) => {
                      setFileList((oldState) => [...oldState, file]);
                      return false;
                    }}
                    onChange={handleUploadChange}
                    multiple={false}
                    listType="picture"
                    accept=".png, .jpg, .jpeg"
                    fileList={fileList}
                  >
                    <Button>
                      <UploadOutlined /> Unggah
                    </Button>
                  </Upload>
                </Form.Item>
              )}
              <Form.Item name="title" label="Judul Berita" validate={validate}>
                <Input name="title" placeholder="Masukkan judul" />
              </Form.Item>
              <Form.Item name="tags" label="Kategori">
                <Select name="tags" mode="tags" placeholder="Masukkan kategori">
                  {newsTags?.map((tag) => (
                    <Option key={tag.id} value={tag.name}>
                      {tag.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {values.newsType !== 'instagram' && (
                <Form.Item name="isHeadline" label="Berita Utama">
                  <Select name="isHeadline">
                    <Option value={true}>Ya</Option>
                    <Option value={false}>Tidak</Option>
                  </Select>
                </Form.Item>
              )}
              <Form.Item name="isPublished" label="Status">
                <Select name="isPublished">
                  <Option value={true}>Terbit</Option>
                  <Option value={false}>Draf</Option>
                </Select>
              </Form.Item>
              <Form.Item name="newsType" label="Tipe Berita">
                <Select name="newsType" placeholder="Pilih tipe berita">
                  <Option value="manual">Berita Manual</Option>
                  <Option value="instagram">Feed Instagram</Option>
                </Select>
              </Form.Item>
              {values.newsType === 'instagram' && (
                <Form.Item name="instagramUrl" label="URL Instagram Post" validate={values.newsType === 'instagram' ? validate : undefined}>
                  <Input name="instagramUrl" placeholder="https://www.instagram.com/p/..." />
                </Form.Item>
              )}
              {values.newsType !== 'instagram' && (
                <Form.Item name="content" label="Isi Berita">
                  <ReactQuill
                    modules={quillConfig}
                    style={{
                      background: 'white',
                    }}
                    onChange={(content) => setContent(content)}
                    value={content}
                  />
                </Form.Item>
              )}
              <SubmitButton>Simpan</SubmitButton>
            </Form>
          )}
        />
      </ContentWithErrorHandler>
    </div>
  );
}

export default NewsDetail;
